import React, { useRef, useState } from "react";
import { Alert, Button, Checkbox, Dropdown, Input, Modal, Tag, message } from "antd";
import ReactSignatureCanvas from "react-signature-canvas";
import { httpClient } from "../services/http.service";

// Same movements and source statuses as the mobile app (Repairs > Collect / Deliver).
// Expired units can be part of any movement and keep their expired status.
export const UNIT_MOVES = [
  { type: "COLLECT FROM CUSTOMER", label: "Collect from customer", from: ["quarantined"], to: "collected" },
  { type: "DROP TO OEM", label: "Drop to OEM", from: ["collected"], to: "rrepair" },
  { type: "COLLECT FROM OEM", label: "Collect from OEM", from: ["repaired", "ber"], to: "enroute" },
  { type: "RETURN TO CUSTOMER", label: "Return to customer", from: ["enroute", "ber"], to: "active" },
];

const resultingStatus = (move, unit) => (unit.status === "expired" ? "expired" : move.to);
const isEligible = (move, unit) => unit.status === "expired" || move.from.includes(unit.status);

// units: the saved units of the RFQ. preselected: units ticked in the table (optional).
export function MoveRFQUnits({ rfqId, units = [], preselected = [], onMoved }) {
  const [move, setMove] = useState(null);
  const [chosenIds, setChosenIds] = useState([]);
  const [comments, setComments] = useState("");
  const [waybillNumber, setWaybillNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const signature = useRef(null);

  const eligible = move ? units.filter((unit) => isEligible(move, unit)) : [];

  const open = (m) => {
    const eligibleUnits = units.filter((unit) => isEligible(m, unit));
    const ticked = preselected.filter((unit) => isEligible(m, unit));
    // ticked units in the table win, otherwise start with everything eligible selected
    setChosenIds((ticked.length ? ticked : eligibleUnits).map((unit) => unit._id));
    setMove(m);
  };

  const close = () => {
    setMove(null);
    setChosenIds([]);
    setComments("");
    setWaybillNumber("");
  };

  const submit = () => {
    if (chosenIds.length === 0) return message.warning("Please select at least one unit.");
    if (!waybillNumber.trim()) return message.warning("Please enter the waybill number.");
    if (!signature.current || signature.current.isEmpty()) return message.warning("Please sign to confirm the movement.");

    setSaving(true);
    httpClient
      .post(`/api/v1/RFQ/${rfqId}/CD`, {
        type: move.type,
        scsrs: chosenIds,
        comments,
        waybillNumber: waybillNumber.trim(),
        collectedFromInformation: signature.current.toDataURL(),
      })
      .then(() => {
        message.success(`${chosenIds.length} unit(s) moved: ${move.label}`);
        setSaving(false);
        close();
        if (onMoved) onMoved();
      })
      .catch((err) => {
        setSaving(false);
        Modal.error({ title: "Move failed", content: err?.response?.data || err?.message });
      });
  };

  // With units ticked in the table the menu looks only at those; otherwise at every unit of the RFQ.
  const items = UNIT_MOVES.map((m) => {
    const scope = preselected.length ? preselected : units;
    const count = scope.filter((unit) => isEligible(m, unit)).length;
    const label =
      count === scope.length
        ? m.label
        : count === 0
        ? `${m.label} (${scope.length === 1 ? "unit" : "units"} not eligible)`
        : `${m.label} (${count} of ${scope.length} eligible)`;
    return { key: m.type, label, disabled: count === 0, onClick: () => open(m) };
  });

  const allChosen = eligible.length > 0 && chosenIds.length === eligible.length;
  const chosenUnits = eligible.filter((unit) => chosenIds.includes(unit._id));

  return (
    <>
      <Dropdown menu={{ items }} trigger={["click"]} disabled={units.length === 0}>
        <Button type="primary">
          Move units{preselected.length ? ` (${preselected.length} ticked)` : ""}
        </Button>
      </Dropdown>

      <Modal
        title={move ? `${move.label} - ${chosenIds.length} of ${eligible.length} unit(s)` : ""}
        open={!!move}
        onCancel={close}
        onOk={submit}
        okText="Confirm move"
        confirmLoading={saving}
        destroyOnClose
      >
        {chosenUnits.some((unit) => unit.status === "expired") && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Expired units will be recorded in this movement but stay EXPIRED."
          />
        )}
        <Checkbox
          checked={allChosen}
          indeterminate={chosenIds.length > 0 && !allChosen}
          onChange={(e) => setChosenIds(e.target.checked ? eligible.map((unit) => unit._id) : [])}
          style={{ fontWeight: 500 }}
        >
          Select all ({eligible.length})
        </Checkbox>
        <div style={{ margin: "8px 0 12px", maxHeight: 180, overflowY: "auto", border: "1px solid #eee", padding: 8 }}>
          {eligible.map((unit) => (
            <div key={unit._id}>
              <Checkbox
                checked={chosenIds.includes(unit._id)}
                onChange={(e) =>
                  setChosenIds((old) => (e.target.checked ? [...old, unit._id] : old.filter((idd) => idd !== unit._id)))
                }
              >
                {unit.serialNumber} <Tag>{unit.status?.toUpperCase()}</Tag>→{" "}
                <Tag color="blue">{resultingStatus(move, unit).toUpperCase()}</Tag>
              </Checkbox>
            </div>
          ))}
        </div>
        <Input
          placeholder="Waybill number"
          value={waybillNumber}
          onChange={(e) => setWaybillNumber(e.target.value)}
          style={{ marginBottom: 8 }}
        />
        <Input.TextArea placeholder="Comments (optional)" value={comments} onChange={(e) => setComments(e.target.value)} rows={2} style={{ marginBottom: 8 }} />
        <p style={{ marginBottom: 4 }}>Signature</p>
        <div style={{ border: "2px solid black" }}>
          <ReactSignatureCanvas penColor="#b6181c" ref={signature} canvasProps={{ height: 150, width: 440 }} />
        </div>
        <Button size="small" style={{ marginTop: 4 }} onClick={() => signature.current?.clear()}>
          Clear signature
        </Button>
      </Modal>
    </>
  );
}
